/** Server-authoritative service timer helpers (Phase F + G). */

export function parseIsoMs(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function computeTimerFromService(service) {
  if (!service) return null;
  const serverMs = parseIsoMs(service.server_now);
  const endMs = parseIsoMs(service.service_scheduled_end_at);
  if (!serverMs || !endMs) return null;

  const phase = service.service_phase;
  if (phase === 'normal_time') {
    const remainingMs = Math.max(0, endMs - serverMs);
    return {
      phase: 'normal_time',
      label: 'Time remaining',
      displaySeconds: Math.floor(remainingMs / 1000),
      heading: 'SERVICE IN PROGRESS',
    };
  }
  if (phase === 'extra_time') {
    const extraMs = Math.max(0, serverMs - endMs);
    return {
      phase: 'extra_time',
      label: 'Extra time',
      displaySeconds: Math.floor(extraMs / 1000),
      heading: 'EXTRA TIME',
      subheading: 'Your included service time has ended.',
    };
  }
  return null;
}

export function extraTimeChargeView(service, offsetMs = 0) {
  if (!service || (service.service_phase !== 'extra_time' && service.service_phase !== 'finished')) {
    return null;
  }
  if (service.service_phase === 'finished') {
    return {
      mode: 'frozen',
      chargeableSeconds: service.chargeable_extra_seconds,
      extraServiceAmount: service.raw_extra_service_amount ?? service.extra_service_amount,
      extraTaxAmount: service.extra_tax_amount,
      extraTaxPercent: service.extra_tax_percent,
      extraTotalAmount: service.final_extra_customer_amount ?? service.extra_total_amount,
      currency: service.currency || 'INR',
    };
  }
  const bufferRemaining = service.free_buffer_remaining_seconds;
  const tickSeconds = Math.floor(offsetMs / 1000);
  if (bufferRemaining != null && bufferRemaining - tickSeconds > 0) {
    return {
      mode: 'buffer',
      bufferRemainingSeconds: Math.max(0, bufferRemaining - tickSeconds),
    };
  }
  return {
    mode: 'chargeable',
    chargeableSeconds: service.chargeable_extra_seconds,
    extraServiceAmount: service.extra_service_amount,
    extraTaxAmount: service.extra_tax_amount,
    extraTaxPercent: service.extra_tax_percent,
    extraTotalAmount: service.extra_total_amount,
    billableBlocks: service.billable_blocks,
    currency: service.currency || 'INR',
  };
}

export function formatTimerSeconds(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function tickTimerState(service, offsetMs = 0) {
  if (!service?.server_now || !service?.service_scheduled_end_at) return null;
  const serverMs = parseIsoMs(service.server_now) + offsetMs;
  const endMs = parseIsoMs(service.service_scheduled_end_at);
  if (!serverMs || !endMs) return null;
  if (serverMs < endMs) {
    return {
      phase: 'normal_time',
      displaySeconds: Math.floor((endMs - serverMs) / 1000),
    };
  }
  return {
    phase: 'extra_time',
    displaySeconds: Math.floor((serverMs - endMs) / 1000),
  };
}

export function formatCurrency(amount, currency = 'INR') {
  if (amount == null) return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Customer-facing service-timing view derived purely from authoritative
 * booking/service timestamps (never a local elapsed counter):
 * - finished  -> final elapsed frozen from service_ended_at/completed_at
 * - running   -> live elapsed anchored to the server clock via the existing
 *                useServiceStatus tick (timer.phase/displaySeconds)
 * - not_started -> service has not begun yet
 */
export function customerServiceTimingView({
  status,
  serviceStartedAt,
  serviceEndedAt,
  completedAt,
  service,
  timer,
} = {}) {
  const startMs = parseIsoMs(serviceStartedAt) ?? parseIsoMs(service?.service_started_at);
  const endedMs = parseIsoMs(serviceEndedAt) ?? parseIsoMs(completedAt);
  if (startMs && endedMs) {
    return {
      state: 'finished',
      displaySeconds: Math.max(0, Math.round((endedMs - startMs) / 1000)),
    };
  }
  const endMs = parseIsoMs(service?.service_scheduled_end_at);
  if (status === 'in_progress' && startMs && endMs && timer) {
    const scheduledTotal = Math.max(0, Math.round((endMs - startMs) / 1000));
    const elapsed = timer.phase === 'extra_time'
      ? scheduledTotal + timer.displaySeconds
      : Math.max(0, scheduledTotal - timer.displaySeconds);
    return { state: 'running', displaySeconds: elapsed };
  }
  if (status === 'in_progress' && startMs) {
    return { state: 'running', displaySeconds: null };
  }
  return { state: 'not_started', displaySeconds: null };
}
